import React from "react"
import classnames from "classnames"

import "./styles.scss"
import useOnClickOutside from "use-onclickoutside"
import { useParams } from "react-router-dom"
import getSelectionsFromParams from "../utils/getSelectionsFromParams"
import DataLoader, { latestYear } from "../DataLoader"
import pdfIcon from "../images/pdfIcon.svg"
import csvIcon from "../images/csvIcon.svg"
import { csvFormat } from "d3-dsv"
const globalLinks = [
  {
    name: "Full Report",
    type: "pdf",
    link: `${process.env.PUBLIC_URL}/downloads/WJPIndex${latestYear}.pdf`,
  },
  {
    name: "All Country Profiles",
    type: "pdf",
    link: `${process.env.PUBLIC_URL}/downloads/CountryProfiles_${latestYear}_WJP_RuleofLawIndex.pdf`,
  },
  {
    name: "Dataset",
    type: "csv",
    link: `${process.env.PUBLIC_URL}/downloads/${latestYear}_wjp_rule_of_law_index_HISTORICAL_DATA_FILE.xlsx`,
  },
  {
    name: "Methodology",
    type: "pdf",
    link: `${process.env.PUBLIC_URL}/downloads/Index-Methodology-${latestYear}.pdf`,
  },
  {
    name: "Variable Map",
    type: "pdf",
    link: `${process.env.PUBLIC_URL}/downloads/ROLIndex${latestYear}_Table_of_Variables.pdf`,
  },
]
// From 2026 on, WJP supplies the per-country profile PDFs; they live in
// public/downloads/profiles/ as <Country_Name>_<year>_country_profile.pdf
// (spaces and apostrophes -> underscores, commas/periods dropped, accents stripped).
// Earlier years used PDFs generated by scripts/pdfgeneration, served from /s/pdfs/.
const FIRST_WJP_PROFILE_YEAR = 2026
const profileSlug = (country) =>
  country
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,]/g, "")
    .replace(/[\s']+/g, "_")
export const countryProfilePdfUrl = (country, year) =>
  Number(year) >= FIRST_WJP_PROFILE_YEAR
    ? `${process.env.PUBLIC_URL}/downloads/profiles/${profileSlug(
        country,
      )}_${year}_country_profile.pdf`
    : `${process.env.PUBLIC_URL}/s/pdfs/${year}-${encodeURIComponent(
        country,
      )}.pdf`

export default function DownloadMenu(props) {
  const { showCountryDownloads, wrapperRef } = props
  useOnClickOutside(wrapperRef, props.toggle)
  const params = useParams()
  const selections = getSelectionsFromParams(params)
  // console.log(selections)
  const linker = ({ name, type, link, onClick, eventLabel }, index) => {
    const click = (e) => {
      if (onClick) {
        onClick(e)
      }
      if (window.gtag) {
        window.gtag("event", "download", {
          event_category: "download",
          event_label: eventLabel || name,
        })
      }
    }
    return (
      <a
        href={link}
        key={index}
        onClick={click}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={type === "pdf" ? pdfIcon : csvIcon} alt={type} />
        <span>{name}</span>
      </a>
    )
  }
  let countrySpecificContent = null
  const downloadDataset = (e) => {
    e.preventDefault()
    e.stopPropagation()

    // console.log(DataLoader)

    const data = []
    const years = Object.keys(DataLoader.dataByYear).sort()
    let yearsWithData = []
    years.forEach((year) => {
      const countryData = DataLoader.dataByYear[year].countries.find(
        (c) => c.Country === selections.selectedCountry,
      )
      if (countryData) {
        yearsWithData.push(year)
        const transposeCountryData = Object.keys(countryData)
          .map((key) => [key, countryData[key]])
          .filter(
            (d) =>
              !d[0].includes("_Rank_") &&
              d[0].indexOf("Count_") !== 0 &&
              d[0].indexOf("overall") !== 0 &&
              d[0] !== "externalPercentChange",
          )
        // console.log(transposeCountryData)
        const keyMatcher = (_key1, _key2) => {
          let key1 = _key1.toLowerCase().trim()
          let key2 = _key2.toLowerCase().trim()

          if (key1 === key2) return true

          const subfactorRE = /^([0-9]\.[0-9])/
          const key1Match = key1.match(subfactorRE)
          const key2Match = key2.match(subfactorRE)

          if (key1Match && key2Match) {
            const key1SubfactorKey = key1Match[1]
            const key2SubfactorKey = key2Match[1]
            // console.log(key1SubfactorKey, key2SubfactorKey)
            if (key1SubfactorKey === key2SubfactorKey) return true
          }

          return false
        }
        transposeCountryData.forEach(([key, value]) => {
          if (!data.find((d) => keyMatcher(d.key, key))) {
            data.push({ key: key.trim() })
          }
          const dataPoint = data.find((d) => keyMatcher(d.key, key))
          dataPoint[year] = value
        })
      }
    })
    yearsWithData.sort()
    // console.log(data)
    const csv = csvFormat(data, ["key", ...yearsWithData])
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute("download", `${selections.selectedCountry}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  if (showCountryDownloads) {
    const pdfUrl = countryProfilePdfUrl(
      showCountryDownloads,
      selections.selectedYear,
    )
    countrySpecificContent = (
      <div className="country-specific">
        <h3>{showCountryDownloads} Downloads</h3>
        {[
          {
            name: "Country Profile",
            type: "pdf",
            link: pdfUrl,
            eventLabel: `${showCountryDownloads} Country Profile`,
          },
          {
            name: "Country Dataset",
            type: "csv",
            link: "#",
            onClick: downloadDataset,
            eventLabel: `${showCountryDownloads} Country Dataset`,
          },
        ].map(linker)}
      </div>
    )
  }
  return (
    <div className={classnames("DownloadMenu")}>
      {globalLinks.map(linker)}
      {countrySpecificContent}
    </div>
  )
}
